import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { addDays, differenceInCalendarDays, format, parseISO, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, CalendarRange, Building2 } from 'lucide-react';
import { useBookings, useProperties, type Booking, type Channel } from '../../../hooks/useManagement';

const DAY_W = 44;          // px per day column
const NAME_W = 200;        // px for the sticky property-name column
const WINDOWS = [30, 45, 60] as const;

const channelBar: Record<Channel, string> = {
  direct: 'bg-teal-500 border-teal-600',
  airbnb: 'bg-rose-500 border-rose-600',
  booking: 'bg-blue-500 border-blue-600',
  vrbo: 'bg-violet-500 border-violet-600',
};

/**
 * Agency helicopter view: a Gantt-style availability timeline. Rows = properties,
 * columns = days, confirmed/pending bookings as bars. Scans the whole portfolio
 * at a glance; click a bar to open that booking.
 */
export default function ManageTimelinePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: properties } = useProperties();
  // All bookings across every property (no active-property filter).
  const { data: bookings } = useBookings('all', null);

  const [start, setStart] = useState<Date>(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [days, setDays] = useState<number>(30);
  const [query, setQuery] = useState('');

  const dayList = useMemo(() => Array.from({ length: days }, (_, i) => addDays(start, i)), [start, days]);
  const windowEnd = useMemo(() => addDays(start, days), [start, days]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (properties ?? []).filter(p =>
      !q || (p.name || '').toLowerCase().includes(q) || (p.city || '').toLowerCase().includes(q));
  }, [properties, query]);

  // Group bookings by property for O(1) row lookup, keeping only those that
  // overlap the visible window and aren't cancelled.
  const byProperty = useMemo(() => {
    const map = new Map<number, Booking[]>();
    for (const b of bookings ?? []) {
      if (b.status === 'cancelled' || b.property_id == null) continue;
      const ci = parseISO(b.check_in), co = parseISO(b.check_out);
      if (co <= start || ci >= windowEnd) continue;    // outside window
      const arr = map.get(b.property_id) ?? [];
      arr.push(b);
      map.set(b.property_id, arr);
    }
    return map;
  }, [bookings, start, windowEnd]);

  const gridW = days * DAY_W;
  const todayOffset = differenceInCalendarDays(new Date(), start);

  return (
    <div className="max-w-full mx-auto space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarRange size={22} className="text-teal-600" />
            {t('portal.manage.timeline.title', 'Calendario multi-propiedad')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('portal.manage.timeline.subtitle', 'Disponibilidad de todo tu portfolio de un vistazo. Haz clic en una reserva para abrirla.')}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white">
          <button onClick={() => setStart(s => addDays(s, -days))} className="p-2 hover:bg-slate-50 rounded-l-lg" aria-label="prev"><ChevronLeft size={16} /></button>
          <button onClick={() => setStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 border-x border-slate-200">
            {t('portal.manage.timeline.today', 'Hoy')}
          </button>
          <button onClick={() => setStart(s => addDays(s, days))} className="p-2 hover:bg-slate-50 rounded-r-lg" aria-label="next"><ChevronRight size={16} /></button>
        </div>
        <span className="text-sm font-semibold text-slate-700 capitalize">
          {format(start, 'd MMM', { locale: es })} – {format(addDays(windowEnd, -1), 'd MMM yyyy', { locale: es })}
        </span>
        <div className="inline-flex items-center rounded-lg bg-slate-100 p-1 ml-auto">
          {WINDOWS.map(w => (
            <button key={w} onClick={() => setDays(w)} className={`px-2.5 py-1 text-xs font-medium rounded-md ${days === w ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`}>
              {w}d
            </button>
          ))}
        </div>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={t('portal.manage.timeline.search', 'Filtrar alojamiento…')}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        {(['direct', 'airbnb', 'booking', 'vrbo'] as Channel[]).map(c => (
          <span key={c} className="inline-flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded ${channelBar[c].split(' ')[0]}`} /> {t(`portal.manage.channel.${c}`)}
          </span>
        ))}
      </div>

      {/* Timeline grid */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <div style={{ width: NAME_W + gridW, minWidth: '100%' }}>
          {/* Header: day columns */}
          <div className="flex sticky top-0 z-20 bg-white border-b border-slate-200">
            <div style={{ width: NAME_W }} className="shrink-0 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400 sticky left-0 bg-white z-10 border-r border-slate-100">
              {t('portal.manage.timeline.property', 'Alojamiento')}
            </div>
            <div className="relative flex" style={{ width: gridW }}>
              {dayList.map((d, i) => {
                const weekend = d.getDay() === 0 || d.getDay() === 6;
                const isToday = differenceInCalendarDays(d, new Date()) === 0;
                return (
                  <div key={i} style={{ width: DAY_W }} className={`shrink-0 text-center py-1.5 border-r border-slate-100 ${weekend ? 'bg-slate-50' : ''}`}>
                    <div className={`text-[10px] uppercase ${isToday ? 'text-teal-600 font-bold' : 'text-slate-400'}`}>{format(d, 'EEE', { locale: es }).slice(0, 2)}</div>
                    <div className={`text-xs ${isToday ? 'text-teal-700 font-bold' : 'text-slate-600'}`}>{format(d, 'd')}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rows */}
          {rows.map(p => {
            const list = byProperty.get(p.id) ?? [];
            return (
              <div key={p.id} className="flex border-b border-slate-100 hover:bg-slate-50/40 group">
                <button
                  onClick={() => navigate(`/portal/manage/properties?edit=${p.id}`)}
                  style={{ width: NAME_W }}
                  className="shrink-0 px-3 py-2 text-left sticky left-0 bg-white group-hover:bg-slate-50 z-10 border-r border-slate-100 flex items-center gap-2.5 min-w-0"
                >
                  <div className="w-9 h-9 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                    {(p as { cover_photo_id?: number }).cover_photo_id
                      ? <img src={`/api/portal/manage/property-media/${(p as { cover_photo_id?: number }).cover_photo_id}`} alt="" loading="lazy" className="w-full h-full object-cover" />
                      : <div className="w-full h-full grid place-items-center text-slate-300"><Building2 size={14} /></div>}
                  </div>
                  <span className="text-sm font-medium text-slate-800 truncate">{p.name || `#${p.id}`}</span>
                </button>
                <div className="relative" style={{ width: gridW, height: 52 }}>
                  {/* day grid lines + weekend shading */}
                  {dayList.map((d, i) => {
                    const weekend = d.getDay() === 0 || d.getDay() === 6;
                    return <div key={i} style={{ left: i * DAY_W, width: DAY_W }} className={`absolute top-0 bottom-0 border-r border-slate-100 ${weekend ? 'bg-slate-50/60' : ''}`} />;
                  })}
                  {/* today line */}
                  {todayOffset >= 0 && todayOffset < days && (
                    <div style={{ left: todayOffset * DAY_W + DAY_W / 2 }} className="absolute top-0 bottom-0 w-px bg-teal-400/70 z-10" />
                  )}
                  {/* booking bars */}
                  {list.map(b => {
                    const ci = parseISO(b.check_in), co = parseISO(b.check_out);
                    const from = Math.max(0, differenceInCalendarDays(ci, start));
                    const to = Math.min(days, differenceInCalendarDays(co, start));
                    const width = Math.max(1, to - from);
                    const pending = b.status === 'pending';
                    return (
                      <button
                        key={b.id}
                        onClick={() => navigate(`/portal/manage/bookings?open=${b.id}`)}
                        title={`${b.guest_name} · ${b.check_in} → ${b.check_out}`}
                        style={{ left: from * DAY_W + 3, width: width * DAY_W - 6, top: 8 }}
                        className={`absolute h-9 rounded-md border text-white text-[11px] font-medium px-2 flex items-center truncate shadow-sm hover:brightness-110 ${channelBar[b.channel] ?? channelBar.direct} ${pending ? 'opacity-60 border-dashed' : ''}`}
                      >
                        <span className="truncate">{b.guest_name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="px-4 py-12 text-center text-slate-400 text-sm">{t('portal.manage.timeline.empty', 'No hay alojamientos que coincidan.')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
